import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { UploadSimple } from "@phosphor-icons/react";
import { UpdateUserDto } from "@reactive-resume/dto";
import { Button, buttonVariants, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, Input, } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { UserAvatar } from "@/client/components/user-avatar";
import { useToast } from "@/client/hooks/use-toast";
import { useUpdatePassword } from "@/client/services/auth";
import { useUploadImage } from "@/client/services/storage";
import { useUpdateUser, useUser } from "@/client/services/user";
import { Eye, EyeSlash } from "@phosphor-icons/react";

// Custom validation schema with proper validations
const updateUserSchema = z.object({
  picture: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  firstName: z
    .string()
    .min(1, "First name is required")
    .regex(/^[a-zA-Z\s]+$/, "First name must contain only letters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .regex(/^[a-zA-Z\s]+$/, "Last name must contain only letters"),
  username: z.string().min(1, "Username is required"),
  email: z.string().email("Invalid email address"),
  phoneNumber: z
    .string()
    .min(1, "This field is required")
    .regex(
      /^\+91 [6-9]\d{4} \d{5}$/,
      "Phone number must be valid and start with 6, 7, 8, or 9"
    ),
});

const passwordFormSchema = z
  .object({
    password: z.string().min(6),
    confirmPassword: z.string().min(6),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: `The passwords you entered do not match.`,
  });

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

export const AccountSettings = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const { updateUser, loading } = useUpdateUser();
  const { uploadImage, loading: isUploading } = useUploadImage();
  const { updatePassword, loading: passwordLoading } = useUpdatePassword();

  const inputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<"personal" | "organization" | "password">("personal");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<UpdateUserDto>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      picture: "",
      name: "",
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      phoneNumber: "",
    },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Check if organization data exists - must be defined before useEffects that use it
  const hasOrganization = useMemo(() => {
    if (!user) return false;
    return Boolean(
      user.organization &&
        (user.organization.department ||
          user.organization.course ||
          user.organization.program ||
          user.organization.batch ||
          user.organization.section)
    );
  }, [user]);

  useEffect(() => {
    user && onReset();
  }, [user]);

  // Log organization data for debugging
  useEffect(() => {
    if (user) {
      console.log("User organization data:", user.organization);
      console.log("Has organization:", hasOrganization);
    }
  }, [user, hasOrganization]);

  const onReset = () => {
    if (!user) return;

    form.reset({
      picture: user.picture ?? "",
      name: user.name,
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      username: user.username,
      email: user.email,
      phoneNumber: user.phoneNumber ?? "",
    });
  };

  const onSubmit = async (data: UpdateUserDto) => {
    if (!user) return;

    // Detect which fields changed
    const changedFields: string[] = [];
    if (user.firstName !== data.firstName) changedFields.push("First Name");
    if (user.lastName !== data.lastName) changedFields.push("Last Name");
    if (user.phoneNumber !== data.phoneNumber) changedFields.push("Phone Number");
    if (user.picture !== data.picture) changedFields.push("Picture");
    if (user.name !== data.name) changedFields.push("Name");

    if (user.email !== data.email) {
      toast({
        variant: "info",
        title: t`Check your email for the confirmation link to update your email address.`,
      });
    }
    const fullName = `${data.firstName} ${data.lastName}`.trim();

    await updateUser({
      name: fullName,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      picture: data.picture,
      username: data.username,
    });

    // Show success message for changed fields
    if (changedFields.length > 0) {
      const fieldsList = changedFields.join(", ");
      toast({
        variant: "success",
        title: t`${fieldsList} updated successfully`,
      });
    }

    form.reset(data);
  };

  const onSelectImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const response = await uploadImage(file);
      const url = response.data;

      await updateUser({ picture: url });

      toast({
        variant: "success",
        title: t`Picture updated successfully`,
      });
    }
  };

  const onPasswordReset = () => {
    passwordForm.reset({
      password: "",
      confirmPassword: "",
    });
  };

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    await updatePassword({ password: data.password });

    toast({
      variant: "success",
      title: t`Your password has been updated successfully.`,
    });

    onPasswordReset();
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-2xl font-bold leading-relaxed tracking-tight">{t`Account Settings`}</h3>
        <p className="leading-relaxed opacity-75">
          {t`Manage your account information and preferences.`}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab("personal")}
            className={cn(
              "pb-4 px-1 border-b-2 font-medium text-sm transition-colors",
              activeTab === "personal"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
            )}
          >
            {t`Personal Details`}
          </button>
          <button
            onClick={() => setActiveTab("organization")}
            className={cn(
              "pb-4 px-1 border-b-2 font-medium text-sm transition-colors",
              activeTab === "organization"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
            )}
          >
            {t`Organization Details`}
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={cn(
              "pb-4 px-1 border-b-2 font-medium text-sm transition-colors",
              activeTab === "password"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
            )}
          >
            {t`Change Password`}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Personal Details Tab */}
        {activeTab === "personal" && (
          <Form {...form}>
            <form className="grid gap-6 sm:grid-cols-2" onSubmit={form.handleSubmit(onSubmit)}>
              {/* Profile Picture */}
              {/* <FormField
                name="picture"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <div
                    className={cn(
                      "flex items-end gap-x-4 sm:col-span-2",
                      error && "items-center"
                    )}
                  >
                    <UserAvatar />
                    <FormItem className="flex-1">
                      <FormLabel>{t`Picture`}</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                    {!user.picture && (
                      <>
                        <input ref={inputRef} hidden type="file" onChange={onSelectImage} />
                        <motion.button
                          disabled={isUploading}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className={cn(buttonVariants({ size: "icon", variant: "ghost" }))}
                          onClick={() => inputRef.current?.click()}
                        >
                          <UploadSimple />
                        </motion.button>
                      </>
                    )}
                  </div>
                )}
              /> */}

              {/* First Name */}
              <FormField
                name="firstName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`First Name`}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Last Name */}
              <FormField
                name="lastName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`Last Name`}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Roll Number - Read Only - Display only, not part of form */}
              {user.rollNo && (
                <FormItem>
                  <FormLabel>{t`Roll Number`}</FormLabel>
                  <FormControl>
                    <Input value={user.rollNo} disabled className="bg-muted" />
                  </FormControl>
                </FormItem>
              )}

              {/* Email - Read Only */}
              <FormField
                name="email"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`Email`}</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} disabled className="bg-muted" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Phone Number */}
              <FormField
                name="phoneNumber"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`Phone Number`}</FormLabel>
                    <FormControl>
                      <Input 
                        type="tel" 
                        value={field.value}
                        placeholder="+91 98765 43210"
                        onChange={(e) => {
                          let value = e.target.value;
                          
                          // If completely empty or just "+91 " or "+91", clear the field
                          if (value === '' || value === '+91' || value === '+91 ') {
                            field.onChange('');
                            return;
                          }
                          
                          // Remove all non-digit characters to get pure digits
                          const allDigits = value.replace(/\D/g, '');
                          
                          // If no digits left after removing non-digits, clear the field
                          if (allDigits === '' || allDigits === '91') {
                            field.onChange('');
                            return;
                          }
                          
                          // Remove the country code '91' if present at the start
                          let userDigits = allDigits;
                          if (allDigits.startsWith('91')) {
                            userDigits = allDigits.slice(2);
                          }
                          
                          // If no user digits after removing country code, clear field
                          if (userDigits === '') {
                            field.onChange('');
                            return;
                          }
                          
                          // Limit to 10 digits
                          userDigits = userDigits.slice(0, 10);
                          
                          // Format as +91 XXXXX XXXXX
                          let formatted = '+91 ';
                          if (userDigits.length <= 5) {
                            formatted += userDigits;
                          } else {
                            formatted += userDigits.slice(0, 5) + ' ' + userDigits.slice(5);
                          }
                          
                          field.onChange(formatted);
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Save/Discard Buttons */}
              <div className="flex items-center space-x-2 self-center sm:col-span-2">
                <Button type="submit" disabled={loading}>
                  {t`Save Changes`}
                </Button>
                <Button type="reset" variant="ghost" onClick={onReset}>
                  {t`Discard`}
                </Button>
              </div>
            </form>
          </Form>
        )}

        {/* Organization Details Tab */}
        {activeTab === "organization" && (
          <>
            {hasOrganization ? (
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground text-gray-500 mb-6">
                    {t`These details are managed by your institution and cannot be changed.`}
                  </p>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  {/* Department */}
                  {user.organization?.department && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t`Department`}
                      </label>
                      <Input value={user.organization.department.name} disabled className="bg-muted" />
                    </div>
                  )}

                  {/* Course */}
                  {user.organization?.course && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t`Course`}
                      </label>
                      <Input value={user.organization.course.name} disabled className="bg-muted" />
                    </div>
                  )}

                  {/* Program */}
                  {user.organization?.program && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t`Program`}
                      </label>
                      <Input value={user.organization.program.name} disabled className="bg-muted" />
                    </div>
                  )}

                  {/* Batch */}
                  {user.organization?.batch && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t`Batch`}
                      </label>
                      <Input value={user.organization.batch.name} disabled className="bg-muted" />
                    </div>
                  )}

                  {/* Section */}
                  {user.organization?.section && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t`Section`}
                      </label>
                      <Input value={user.organization.section.name} disabled className="bg-muted" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground">
                  {t`No organization details available.`}
                </p>
              </div>
            )}
          </>
        )}

        {/* Change Password Tab */}
        {activeTab === "password" && (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground mb-6">
                {t`Update your password to keep your account secure.`}
              </p>
            </div>

            <Form {...passwordForm}>
              <form
                className="grid gap-6 sm:grid-cols-2"
                onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
              >
                <FormField
                  name="password"
                  control={passwordForm.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t`New Password`}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            {...field}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? (
                              <Eye size={18} />
                            ) : (
                              <EyeSlash size={18} />
                            )}
                          </button>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  name="confirmPassword"
                  control={passwordForm.control}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t`Confirm New Password`}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            {...field}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showConfirmPassword ? (
                              <Eye size={18} />
                            ) : (
                              <EyeSlash size={18} />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      {fieldState.error && (
                        <FormDescription className="text-error">
                          {fieldState.error.message}
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />

                <div className="flex items-center space-x-2 sm:col-span-2">
                  <Button type="submit" disabled={passwordLoading}>
                    {t`Change Password`}
                  </Button>
                  <Button type="reset" variant="ghost" onClick={onPasswordReset}>
                    {t`Discard`}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </div>
    </div>
  );
};